import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { Alert, CircularProgress, IconButton, Menu, MenuItem, Snackbar } from '@mui/material';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { getApiBase } from '../../utils/apiBase';
import { createReportClient, reportCopy, reportErrorMessage, reportQuery } from '../../utils/reporting';

const ReportingContext = createContext({ enabled: false });
const API_BASE = getApiBase();

export function ReportingProvider({ children }) {
  const { userId, getToken } = useAuth();
  const { i18n } = useTranslation();
  const copy = reportCopy(i18n.resolvedLanguage || i18n.language);
  const [capability, setCapability] = useState(null);
  const [notice, setNotice] = useState(null);
  const session = useRef(null);
  if (!session.current || session.current.userId !== userId) session.current = { userId, client: createReportClient(() => {
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  }) };
  const enabled = Boolean(userId && capability?.userId === userId && capability.enabled);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    setNotice(null);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    if (userId) Promise.resolve(getToken()).then(token => {
      if (!token || controller.signal.aborted) throw new Error('auth_required');
      return fetch(`${API_BASE}/reports/capabilities`, { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } });
    })
      .then(response => response.ok ? response.json() : null)
      .then(data => { if (!controller.signal.aborted) setCapability({ userId, enabled: data?.reportingEnabled === true }); })
      .catch(() => {}).finally(() => clearTimeout(timeout));
    return () => { alive.current = false; clearTimeout(timeout); controller.abort(); };
  }, [userId, getToken]);

  const submit = async (target, coordinates) => {
    const current = session.current;
    try {
      await current.client.submit(target, async (body, key) => {
        const token = await getToken();
        if (!enabled || !token || session.current !== current) throw Object.assign(new Error('auth_required'), { status: 401 });
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        try {
          const response = await fetch(`${API_BASE}/reports${reportQuery(coordinates)}`, {
            method: 'POST', signal: controller.signal,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'Idempotency-Key': key },
            body: JSON.stringify(body),
          });
          const data = await response.json();
          if (!response.ok) throw Object.assign(new Error('report_failed'), { status: response.status, code: data?.error });
          return data;
        } finally { clearTimeout(timeout); }
      });
      if (alive.current && session.current === current) setNotice({ message: copy.success, severity: 'success' });
      return true;
    } catch (error) {
      if (alive.current && session.current === current) setNotice({ message: reportErrorMessage(error, copy), severity: 'error' });
      return false;
    }
  };

  return <ReportingContext.Provider value={{ enabled, submit, copy }}>
    {children}
    <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={(_, reason) => reason !== 'clickaway' && setNotice(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ zIndex: 20000, bottom: 'calc(24px + env(safe-area-inset-bottom)) !important' }}>
      <Alert severity={notice?.severity || 'success'} onClose={() => setNotice(null)}>{notice?.message}</Alert>
    </Snackbar>
  </ReportingContext.Provider>;
}

export function ReportMenuItem({ targetType, targetId, coordinates, onDone }) {
  const { enabled, submit, copy } = useContext(ReportingContext);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  if (!enabled || !targetId) return null;
  return <MenuItem disabled={busy} onClick={async event => {
    event.stopPropagation();
    if (lock.current) return;
    lock.current = true; setBusy(true);
    const success = await submit({ targetType, targetId }, coordinates);
    if (!mounted.current) return;
    lock.current = false; setBusy(false);
    if (success) onDone?.();
  }} sx={{ gap: 1, color: '#fff', minHeight: 44 }}>
    {busy ? <CircularProgress size={18} color="inherit" /> : <FlagOutlinedIcon fontSize="small" />}{copy.report}
  </MenuItem>;
}

export function ReportMenu({ targetType, targetId, coordinates, onOpenChange, variant = 'more' }) {
  const { enabled, copy } = useContext(ReportingContext);
  const [anchor, setAnchor] = useState(null);
  const callback = useRef(onOpenChange);
  callback.current = onOpenChange;
  useEffect(() => {
    callback.current?.(Boolean(anchor));
    return () => { if (anchor) callback.current?.(false); };
  }, [anchor]);
  useEffect(() => { setAnchor(null); }, [targetType, targetId, enabled]);
  if (!enabled || !targetId) return null;
  return <>
    <IconButton aria-label={variant === 'flag' ? copy.report : copy.more} aria-haspopup="menu" aria-expanded={Boolean(anchor)} onClick={event => { event.stopPropagation(); setAnchor(event.currentTarget); }} onMouseDown={event => event.stopPropagation()} onMouseUp={event => event.stopPropagation()} onTouchStart={event => event.stopPropagation()} onTouchEnd={event => event.stopPropagation()} sx={{ color: variant === 'flag' ? '#BFC2CC' : '#fff', flexShrink: 0, width: variant === 'flag' ? 32 : 36, height: variant === 'flag' ? 32 : 36 }}>
      {variant === 'flag' ? <FlagOutlinedIcon sx={{ fontSize: 14 }} /> : <MoreHorizRoundedIcon />}
    </IconButton>
    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} onClick={event => event.stopPropagation()} onMouseDown={event => event.stopPropagation()} onMouseUp={event => event.stopPropagation()} onTouchStart={event => event.stopPropagation()} onTouchEnd={event => event.stopPropagation()} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} sx={{ zIndex: 19000 }} PaperProps={{ sx: { bgcolor: '#18181c', color: '#fff', borderRadius: '5px', minWidth: 150 } }}>
      <ReportMenuItem key={`${targetType}:${targetId}`} targetType={targetType} targetId={targetId} coordinates={coordinates} onDone={() => setAnchor(null)} />
    </Menu>
  </>;
}
